// Spectator-only header chunk: "WATCHING" plus a two-button toggle for
// switching perspective between seat 0 and seat 1. Labels each side
// with the seated player's name when available, otherwise generic
// "Player 1" / "Player 2". Hidden for seated players (their own seat
// is implied by `snapshot.viewer`).

import type { GameView, PlayerId } from "../../api/types";
import type { pixelTheme } from "../../theme/pixel";
import { defaultBtn, primaryBtn } from "../../theme/styles";

type SeatPersonas = Record<"0" | "1", { name: string | null } | undefined>;

export function spectatorHeadline(
  result: NonNullable<GameView["result"]>,
  snapshot: GameView,
): string {
  const personas = (snapshot as { seat_personas?: SeatPersonas })?.seat_personas;
  const nameFor = (seat: 0 | 1): string =>
    personas?.[String(seat) as "0" | "1"]?.name ??
    (seat === 0 ? "Player 1" : "Player 2");
  if (result.kind === "draw") return "Draw";
  if (result.kind === "winner") return `${nameFor(result.player as 0 | 1)} wins`;
  return "Game over";
}

export function SpectatorHeader({
  snapshot,
  perspective,
  onPick,
  theme,
}: {
  snapshot: GameView | null;
  perspective: PlayerId;
  onPick: (seat: PlayerId) => void;
  theme: typeof pixelTheme;
}) {
  const personas = (snapshot as { seat_personas?: SeatPersonas } | null)
    ?.seat_personas;
  const nameFor = (seat: 0 | 1): string => {
    const fromMap = personas?.[String(seat) as "0" | "1"]?.name;
    return fromMap ?? (seat === 0 ? "Player 1" : "Player 2");
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 11,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
        }}
      >
        WATCHING
      </span>
      <button
        type="button"
        onClick={() => onPick(0)}
        aria-pressed={perspective === 0}
        {...(perspective === 0
          ? primaryBtn(theme, false)
          : defaultBtn(theme, false))}
      >
        {nameFor(0)}
      </button>
      <button
        type="button"
        onClick={() => onPick(1)}
        aria-pressed={perspective === 1}
        {...(perspective === 1
          ? primaryBtn(theme, false)
          : defaultBtn(theme, false))}
      >
        {nameFor(1)}
      </button>
    </div>
  );
}
